/* eslint-disable react/jsx-no-target-blank */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import { register } from '../core/_requests'
import { Link } from 'react-router-dom'
import { useAuth } from '../core/Auth'
import { Eye, EyeOff, Loader2, User, Mail, Lock, Building2, AlertCircle } from 'lucide-react'

interface PasswordStrength {
  score: 0 | 1 | 2 | 3 | 4
  label: string
  color: string
}

function evaluatePassword(password: string): PasswordStrength {
  let score = 0
  if (password.length >= 8) score++
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++
  if (/\d/.test(password)) score++
  if (/[^A-Za-z0-9]/.test(password)) score++
  const map: Record<number, PasswordStrength> = {
    0: { score: 0, label: 'Too short', color: 'bg-slate-200' },
    1: { score: 1, label: 'Weak', color: 'bg-red-500' },
    2: { score: 2, label: 'Fair', color: 'bg-amber-500' },
    3: { score: 3, label: 'Strong', color: 'bg-blue-500' },
    4: { score: 4, label: 'Excellent', color: 'bg-emerald-500' },
  }
  return map[score]
}

const initialValues = {
  firstname: '',
  lastname: '',
  email: '',
  password: '',
  changepassword: '',
  acceptTerms: false,
}

const registrationSchema = Yup.object().shape({
  firstname: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('First name is required'),
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  lastname: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Last name is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
  changepassword: Yup.string()
    .required('Password confirmation is required')
    .when('password', {
      is: (val: string) => (val && val.length > 0 ? true : false),
      then: Yup.string().oneOf([Yup.ref('password')], "Password and Confirm Password didn't match"),
    }),
  acceptTerms: Yup.bool().required('You must accept the terms and conditions'),
})

export function Registration() {
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const { saveAuth } = useAuth()

  const formik = useFormik({
    initialValues,
    validationSchema: registrationSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      try {
        const { data: auth } = await register(
          values.email,
          values.firstname,
          values.lastname,
          values.password,
          values.changepassword
        )
        saveAuth(auth)
      } catch (error) {
        console.error(error)
        saveAuth(undefined)
        setStatus('The registration details is incorrect')
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  const strength = evaluatePassword(formik.values.password)

  return (
    <form
      noValidate
      id='kt_login_signup_form'
      onSubmit={formik.handleSubmit}
    >
      <h2 className="text-2xl font-bold text-slate-900">Create your account</h2>
      <p className="mt-1 text-sm text-slate-500">Start a free 14-day trial. No credit card required.</p>

      <div className="mt-7 space-y-4">
        {/* Error display */}
        {formik.status && (
          <div
            role="alert"
            className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-red-200 bg-red-50 text-xs text-red-700"
          >
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{formik.status}</span>
          </div>
        )}

        {/* First name & Last name */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="firstname" className="block text-xs font-medium text-slate-700 mb-1.5">
              First name
            </label>
            <div className="relative">
              <User className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                id="firstname"
                type="text"
                required
                autoComplete="given-name"
                placeholder="First name"
                className={clsx(
                  'w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border bg-white shadow-sm placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100',
                  formik.touched.firstname && formik.errors.firstname ? 'border-red-300' : 'border-slate-200'
                )}
                {...formik.getFieldProps('firstname')}
              />
            </div>
            {formik.touched.firstname && formik.errors.firstname && (
              <p className="mt-1 text-xs text-red-600">{formik.errors.firstname}</p>
            )}
          </div>

          <div>
            <label htmlFor="lastname" className="block text-xs font-medium text-slate-700 mb-1.5">
              Last name
            </label>
            <div className="relative">
              <User className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                id="lastname"
                type="text"
                required
                autoComplete="family-name"
                placeholder="Last name"
                className={clsx(
                  'w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border bg-white shadow-sm placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100',
                  formik.touched.lastname && formik.errors.lastname ? 'border-red-300' : 'border-slate-200'
                )}
                {...formik.getFieldProps('lastname')}
              />
            </div>
            {formik.touched.lastname && formik.errors.lastname && (
              <p className="mt-1 text-xs text-red-600">{formik.errors.lastname}</p>
            )}
          </div>
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-xs font-medium text-slate-700 mb-1.5">
            Work email
          </label>
          <div className="relative">
            <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              placeholder="you@company.com"
              className={clsx(
                'w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border bg-white shadow-sm placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100',
                formik.touched.email && formik.errors.email ? 'border-red-300' : 'border-slate-200'
              )}
              {...formik.getFieldProps('email')}
            />
          </div>
          {formik.touched.email && formik.errors.email && (
            <p className="mt-1 text-xs text-red-600">{formik.errors.email}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <label htmlFor="password" className="block text-xs font-medium text-slate-700 mb-1.5">
            Password
          </label>
          <div className="relative">
            <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              required
              autoComplete="new-password"
              placeholder="At least 8 characters"
              className={clsx(
                'w-full pl-9 pr-10 py-2.5 text-sm rounded-lg border bg-white shadow-sm placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100',
                formik.touched.password && formik.errors.password ? 'border-red-300' : 'border-slate-200'
              )}
              {...formik.getFieldProps('password')}
            />
            <button
              type="button"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {formik.touched.password && formik.errors.password && (
            <p className="mt-1 text-xs text-red-600">{formik.errors.password}</p>
          )}
          {/* Password strength meter */}
          {formik.values.password.length > 0 && (
            <div className="mt-2">
              <div className="flex gap-1">
                {[0, 1, 2, 3].map((i) => (
                  <span
                    key={i}
                    className={`h-1.5 flex-1 rounded-full ${i < strength.score ? strength.color : 'bg-slate-200'}`}
                  ></span>
                ))}
              </div>
              <p className="mt-1 text-[11px] text-slate-500">
                Strength: <span className="font-medium text-slate-700">{strength.label}</span>
              </p>
            </div>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label htmlFor="changepassword" className="block text-xs font-medium text-slate-700 mb-1.5">
            Confirm Password
          </label>
          <div className="relative">
            <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              id="changepassword"
              type="password"
              required
              autoComplete="new-password"
              placeholder="Password confirmation"
              className={clsx(
                'w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border bg-white shadow-sm placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100',
                formik.touched.changepassword && formik.errors.changepassword ? 'border-red-300' : 'border-slate-200'
              )}
              {...formik.getFieldProps('changepassword')}
            />
          </div>
          {formik.touched.changepassword && formik.errors.changepassword && (
            <p className="mt-1 text-xs text-red-600">{formik.errors.changepassword}</p>
          )}
        </div>

        {/* Terms checkbox */}
        <label className="flex items-start gap-2 select-none cursor-pointer">
          <input
            type="checkbox"
            id='kt_login_toc_agree'
            className="w-4 h-4 mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-2 focus:ring-blue-100"
            {...formik.getFieldProps('acceptTerms')}
          />
          <span className="text-xs text-slate-600 leading-relaxed">
            I agree to the{' '}
            <a
              href='https://keenthemes.com/metronic/?page=faq'
              target='_blank'
              className='text-blue-600 hover:underline font-medium'
            >
              Terms
            </a>
            .
          </span>
        </label>
        {formik.touched.acceptTerms && formik.errors.acceptTerms && (
          <p className="text-xs text-red-600">{formik.errors.acceptTerms}</p>
        )}

        {/* Submit */}
        <button
          type="submit"
          id='kt_sign_up_submit'
          disabled={formik.isSubmitting || !formik.isValid || !formik.values.acceptTerms}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold shadow-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {loading ? 'Please wait…' : 'Submit'}
        </button>

        <Link to='/auth/login'>
          <button
            type="button"
            id='kt_login_signup_form_cancel_button'
            className="w-full px-4 py-2.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
        </Link>
      </div>

      <p className="mt-7 text-center text-sm text-slate-600">
        Already have an account?{' '}
        <Link to="/auth/login" className="text-blue-600 font-semibold hover:underline">
          Sign in
        </Link>
      </p>
    </form>
  )
}
